import React, { useState, useEffect } from 'react';
import { StepProps, PLANS } from '../types/stepTypes';
import { useOnboardingDataRedux } from '../../hooks/useOnboardingDataRedux';

interface PricingStepProps extends StepProps {
  selectedPlan: string;
  setSelectedPlan: (plan: string) => void;
}

const PricingStep: React.FC<PricingStepProps> = ({
  currentStep,
  setCurrentStep,
  completedSteps,
  setCompletedSteps,
  selectedPlan,
  setSelectedPlan
}) => {
  const { updateData, shouldPreventPlanChange, hasPaymentCompleted, data: onboardingData, restoreFromBackup } = useOnboardingDataRedux();
  
  // State for billing cycle and plan data
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('yearly'); // Default to yearly
  const [availablePlans, setAvailablePlans] = useState<any[]>([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);

  // Get actual plan data from database
  const getPlanFromDB = (planType: string, cycle: 'monthly' | 'yearly') => {
    const planId = cycle === 'yearly' ? `${planType.toLowerCase()}_yearly` : `${planType.toLowerCase()}_monthly`;
    const plan = availablePlans.find(p => (p.planId || p.id) === planId);
    
    console.log(`🔍 [PricingStep] Looking for plan: ${planId}`);
    console.log(`📋 [PricingStep] Available plans:`, availablePlans.map(p => ({ id: p.planId || p.id, sms_limit: p.sms_limit })));
    console.log(`📊 [PricingStep] Found plan:`, plan);
    
    if (plan) {
      const messageLimit = plan.sms_limit || getMessageLimitFromFeatures(plan.features);
      console.log(`💬 [PricingStep] Message limit for ${planId}: ${messageLimit} (sms_limit: ${plan.sms_limit})`);
      
      return {
        id: planType.toLowerCase(),
        price: plan.price,
        features: plan.features || [],
        description: plan.description,
        messageLimit: messageLimit, // Use sms_limit from DB first
        name: planType,
        popular: planType === 'Standard'
      };
    }
    
    // Fallback if plan not found in DB
    console.log(`⚠️ [PricingStep] Plan ${planId} not found, using fallback`);
    return {
      id: planType.toLowerCase(),
      price: planType === 'Starter' ? 29 : planType === 'Standard' ? 49 : 99,
      features: getDefaultFeatures(planType),
      description: getDefaultDescription(planType),
      messageLimit: planType === 'Starter' ? 750 : planType === 'Standard' ? 1750 : 4000,
      name: planType,
      popular: planType === 'Standard'
    };
  };

  // Extract message limit from features array
  const getMessageLimitFromFeatures = (features: string[] = []) => {
    const messageFeature = features.find(f => f.includes('messages') || f.includes('_messages_'));
    if (messageFeature) {
      const numbers = messageFeature.match(/\d+/g);
      if (numbers && numbers.length > 0) {
        return parseInt(numbers[0]);
      }
    }
    return 750; // default fallback
  };

  // Default features fallback
  const getDefaultFeatures = (planType: string) => {
    if (planType === 'Starter') {
      return ['750 messages included', 'Unlimited AI conversations', 'Basic mood tracking', 'Mobile app access'];
    } else if (planType === 'Standard') {
      return ['1,750 messages included', 'Everything in Starter', 'Advanced analytics & insights', 'Goal setting & tracking'];
    } else {
      return ['4,000 messages included', 'Everything in Standard', 'Direct therapist consultations', 'Personalized treatment plans'];
    }
  };

  // Default description fallback
  const getDefaultDescription = (planType: string) => {
    if (planType === 'Starter') return 'Perfect for getting started';
    if (planType === 'Standard') return 'Most comprehensive support';
    return 'For serious mental health focus';
  };

  // Fetch available plans from database
  const fetchAvailablePlans = async () => {
    setIsLoadingPlans(true);
    try {
      const API_BASE = (() => {
        const prodUrl = 'https://us-central1-silas-5fdd2.cloudfunctions.net/api';
        const localUrl = 'http://localhost:5001';
        
        if (process.env.REACT_APP_USE_PROD_API === 'true') {
          return prodUrl;
        }
        
        if (process.env.NODE_ENV === 'production') {
          return prodUrl;
        }
        
        return localUrl;
      })();
        
      const response = await fetch(`${API_BASE}/plans`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      
      if (result.success && result.plans) {
        setAvailablePlans(result.plans);
      } else {
        setAvailablePlans([]);
      }
    } catch (error) {
      console.error('❌ Error fetching plans:', error);
      setAvailablePlans([]);
    } finally {
      setIsLoadingPlans(false);
    }
  };
  
  // Fetch plans when component mounts
  useEffect(() => {
    fetchAvailablePlans();
  }, []);

  // Auto-restore from backup if we should have plan data but don't
  React.useEffect(() => {
    const shouldHaveData = onboardingData.currentStep === 2 && 
                          onboardingData.paymentStatus === 'completed' && 
                          !onboardingData.selectedPlan;
    
    if (shouldHaveData) {
      console.log('🔄 PricingStep: Auto-restoring from backup');
      restoreFromBackup();
    }
  }, [onboardingData.currentStep, onboardingData.paymentStatus, onboardingData.selectedPlan, restoreFromBackup]);
  
  const handlePlanSelect = (planType: string) => {
    // Prevent plan changes if payment has been completed but account creation failed
    if (shouldPreventPlanChange()) {
      return;
    }
    
    const planId = billingCycle === 'yearly' ? `${planType.toLowerCase()}_yearly` : `${planType.toLowerCase()}_monthly`;
    const planData = getPlanFromDB(planType, billingCycle);
    
    setSelectedPlan(planId);
    const newCompleted = [...completedSteps];
    newCompleted[1] = true;
    setCompletedSteps(newCompleted);
    
    // Update persistent data
    updateData({
      selectedPlan: planId,
      planPrice: planData.price,
      planFeatures: planData.features,
    });
  };

  if (currentStep !== 2) return null;

  return (
    <div className="max-w-4xl mx-auto px-2 sm:px-4">
      <div className="text-center mb-6 sm:mb-8">
        <h2 className="text-xl sm:text-2xl font-bold text-black mb-3" style={{ fontFamily: "'Canela', serif", fontWeight: 700, letterSpacing: '-0.02em' }}>
          Choose Your Plan
        </h2>
        <p className="text-sm sm:text-base text-gray-600 mb-4">
          Select the plan that best supports your mental health journey
        </p>
        <div className="py-3 px-4 sm:px-6 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-sm sm:text-base text-gray-700 font-medium">
            All plans include 2-way SMS messaging • Overage: $0.015/message
          </p>
        </div>
      </div>

      {/* Billing Cycle Toggle */}
      <div className="flex justify-center mb-8">
        <div className="bg-gray-100 p-1 rounded-lg inline-flex">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-6 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
              billingCycle === 'monthly'
                ? 'bg-black text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            MONTHLY
          </button>
          <button
            onClick={() => setBillingCycle('yearly')}
            className={`px-6 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
              billingCycle === 'yearly'
                ? 'bg-black text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            YEARLY (20% Off)
          </button>
        </div>
      </div>
      
      {/* Responsive Grid: Stack on mobile, side-by-side on larger screens */}
      {isLoadingPlans ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-gray-300 border-t-black rounded-full animate-spin mr-3"></div>
          <p className="text-gray-600">Loading plans...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mb-6">
          {['Starter', 'Standard', 'Pro'].map((planType) => {
            const planData = getPlanFromDB(planType, billingCycle);
            const planId = billingCycle === 'yearly' ? `${planType.toLowerCase()}_yearly` : `${planType.toLowerCase()}_monthly`;
            const isSelected = selectedPlan === planId;
            
            return (
              <div
                key={planId}
                className={`relative cursor-pointer transition-all duration-300 ${
                  isSelected ? 'scale-[1.02] lg:scale-[1.05]' : 'hover:scale-[1.01]'
                }`}
                onClick={() => handlePlanSelect(planType)}
              >
                {planData.popular && (
                  <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 z-10">
                    <div className="bg-black text-white text-xs sm:text-sm px-3 py-1 rounded-full shadow-lg font-medium">
                      POPULAR
                    </div>
                  </div>
                )}
                
                <div className={`h-full relative overflow-hidden rounded-lg sm:rounded-xl transition-all duration-300 ${
                  isSelected
                    ? 'bg-black text-white border-2 border-black shadow-lg'
                    : 'bg-white border border-gray-200 hover:border-gray-300 hover:shadow-md'
                }`}>
                  <div className="p-4 sm:p-6">
                    <div className="text-center mb-6">
                      <h3 className={`text-lg sm:text-xl font-bold mb-2 ${
                        isSelected ? 'text-white' : 'text-black'
                      }`} style={{ fontWeight: 600 }}>
                        {planData.name}
                      </h3>
                      <p className={`text-sm sm:text-base mb-4 ${
                        isSelected ? 'text-gray-200' : 'text-gray-600'
                      }`}>
                        {planData.description}
                      </p>
                      <div className="mb-4">
                        <span className={`text-2xl sm:text-3xl font-bold ${
                          isSelected ? 'text-white' : 'text-black'
                        }`} style={{ fontWeight: 700 }}>
                          ${billingCycle === 'yearly' ? planData.price * 12 : planData.price}
                        </span>
                        <span className={`text-sm sm:text-base ml-1 ${
                          isSelected ? 'text-gray-200' : 'text-gray-500'
                        }`}>
                          /{billingCycle === 'yearly' ? 'year' : 'month'}
                        </span>
                     
                      </div>
                    
                      {/* Message limit highlight */}
                      <div className={`py-2 px-3 rounded-lg border ${
                        isSelected 
                          ? 'bg-gray-800 border-gray-600' 
                          : 'bg-gray-50 border-gray-200'
                      }`}>
                        <p className={`text-sm font-semibold ${
                          isSelected ? 'text-gray-200' : 'text-gray-700'
                        }`}>
                          {planData.messageLimit.toLocaleString()} messages included
                        </p>
                      </div>

                      {billingCycle === 'yearly' && (
                          <div className={`py-2 my-2 px-3 rounded-lg border ${
                            isSelected 
                              ? 'bg-gray-800 border-gray-600' 
                              : 'bg-gray-50 border-gray-200'
                          }`}>
                            <p className={`text-sm font-semibold ${
                              isSelected ? 'text-gray-200' : 'text-gray-700'
                            }`}>
                           ${planData.price} per month

                            </p>
                          </div>
                        )}
                    </div>
                    
                    <ul className="space-y-3 mb-6">
                      {planData.features.map((feature: string, index: number) => (
                        <li key={index} className={`flex items-start gap-3 ${
                          isSelected ? 'text-gray-200' : 'text-gray-700'
                        }`}>
                          <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${
                            isSelected ? 'bg-white' : 'bg-black'
                          }`}>
                            <svg className={`w-3 h-3 ${
                              isSelected ? 'text-black' : 'text-white'
                            }`} fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <span className="text-sm leading-relaxed">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <button
                      className={`w-full py-3 px-4 rounded-lg text-sm font-medium transition-all duration-300 ${
                        isSelected
                          ? 'bg-white text-black shadow-md hover:bg-gray-100'
                          : 'border-2 border-gray-300 text-gray-700 hover:border-black hover:bg-gray-50'
                      }`}
                    >
                      {isSelected ? (
                        <span className="flex items-center justify-center gap-2">
                          Selected
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </span>
                      ) : (
                        'Select Plan'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      <div className="flex justify-between items-center mt-8 mb-4">
        <button
          onClick={() => setCurrentStep?.(1)}
          className="px-6 py-3 text-sm border border-gray-300 text-gray-600 rounded-lg font-medium 
                     hover:border-gray-400 hover:bg-gray-50 transition-all duration-300 
                     flex items-center gap-2 group"
        >
          <span className="transform group-hover:-translate-x-1 transition-transform duration-300">←</span>
          Back
        </button>
        <button
          onClick={() => {
            if (selectedPlan) {
              const newCompletedSteps = [...completedSteps];
              newCompletedSteps[1] = true;
              
              // Extract plan type from selected plan ID
              const planType = selectedPlan.replace('_monthly', '').replace('_yearly', '');
              const planData = getPlanFromDB(planType.charAt(0).toUpperCase() + planType.slice(1), billingCycle);
              
              // Update persistent data and move to next step
              updateData({
                currentStep: 3,
                completedSteps: newCompletedSteps,
                selectedPlan,
                planPrice: planData.price,
                planFeatures: planData.features,
              });
              
              // Also update local state for immediate UI response
              setCompletedSteps(newCompletedSteps);
              setCurrentStep?.(3);
            }
          }}
          disabled={!selectedPlan}
          className={`px-6 py-3 rounded-lg text-sm font-medium transition-all duration-300 
                     flex items-center gap-2 group ${
            selectedPlan
              ? 'bg-black text-white hover:bg-gray-800 shadow-md hover:shadow-lg'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue
          <span className="transform group-hover:translate-x-1 transition-transform duration-300">→</span>
        </button>
      </div>
    </div>
  );
};

export default PricingStep;